//@ts-ignore
import useResizeAware from "react-resize-aware";

import { BybAnimatedBorder, BybConditionalWrapper } from "@Components/common/byb-ds";
import CardStatusIcon from "@Components/game/cards/card-status-icon";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import { faCheck, faClose, faLockAlt } from "@fortawesome/pro-solid-svg-icons";
import useChallengeCardState from "@hooks/game/use-challenge-card-state";
import useI18n from "@i18n";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { ChallengeResponse } from "@Shared/schemas/challenges/challengeSchemas";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BetaTag,
  ChallengeCardBlur,
  ChallengeCardSimpleButtonBase,
  ChallengeCardSimpleGlossWrapper,
  ChallengeCardSimpleImage,
  ChallengeCardSimpleTopRight,
  InfoTag,
  NotPublishedTag,
} from "./styled-components";
import { challengeHasTag, formatTimeUntil } from "./utils";

export interface IChallengeCardSimpleBase {
  size?: "small" | "medium";
  forceMaxWidth?: boolean;
}

interface IChallengeCardSimple extends IChallengeCardSimpleBase {
  challenge: ChallengeResponse;
  disabled?: boolean;
  fullWidth?: boolean;
  animatedBorder?: boolean;
  onClick?: () => void;
}

const ChallengeCardSimple = ({
  challenge,
  disabled = false,
  size = "medium",
  fullWidth = false,
  forceMaxWidth = true,
  animatedBorder = false,
  onClick,
}: IChallengeCardSimple) => {
  const { t } = useTranslation("VR");
  const { i18n } = useI18n();
  const theme = useTheme();
  const width = size === "small" ? 318 : 432;
  const height = size === "small" ? 180 : 244;
  const { id, image_url, title, start_at, end_at, published, tags } = challenge;
  const [resizeListener, sizes] = useResizeAware();
  const { isDisabled, isCompleted, isFailed, noDemoAccess, league, division, locizeKeyPrefix, handleNavigate } = useChallengeCardState({
    challenge,
    disabled,
  });
  const [startTime] = useState(start_at ? formatTimeUntil(start_at, i18n) : null);
  const [endTime] = useState(end_at ? formatTimeUntil(end_at, i18n) : null);
  const countDown = startTime || endTime;
  const hasEnded = end_at && new Date(end_at).getTime() < Date.now();
  const hasNotStarted = start_at && new Date(start_at).getTime() > Date.now();
  let challengeStatusTest = i18n("challenges.card.ends-in", { time: endTime });
  if (hasNotStarted) challengeStatusTest = i18n("challenges.card.start-in", { time: startTime });
  if (hasEnded) challengeStatusTest = i18n("challenges.card.has-ended");
  const resizeHeight = Math.max((sizes?.width * 9) / 18, height);

  return (
    <HapticsButtonWrapper>
      <ChallengeCardSimpleButtonBase
        id={id.toString()}
        onClick={onClick || handleNavigate}
        disabled={isDisabled}
        sx={{ width: "100%", maxWidth: forceMaxWidth ? width : "none", alignSelf: "flex-start" }}
      >
        {resizeListener}
        <BybConditionalWrapper
          condition={animatedBorder}
          trueWrapper={children => (
            <BybAnimatedBorder
              borderRadius={theme.spacing(2.5)}
              borderWidth={2}
              color={theme.palette.dsPrimary.v300}
              sx={{ width: "100%", display: "block", boxSizing: "border-box", zIndex: 3 }}
            >
              {children}
            </BybAnimatedBorder>
          )}
        >
          <Box sx={{ cornerShape: "squircle", borderRadius: "40px", height: resizeHeight, position: "relative", overflow: "hidden" }}>
            <ChallengeCardSimpleGlossWrapper id="wrapper" height={resizeHeight} width={fullWidth ? "100%" : width}>
              <ChallengeCardSimpleImage id="backgroundImage" imageUrl={image_url} disabled={isDisabled} />
            </ChallengeCardSimpleGlossWrapper>
            {isDisabled && <ChallengeCardBlur />}
            {(isDisabled || isCompleted || isFailed) && (
              <CardStatusIcon
                icon={isDisabled ? faLockAlt : isCompleted ? faCheck : faClose}
                color={!isDisabled ? theme.palette[isCompleted ? "dsSuccess" : "dsDestructive"].v300 : undefined}
              />
            )}
            {(countDown || hasEnded) && (
              <ChallengeCardSimpleTopRight sx={{ cornerShape: "squircle", borderRadius: "16px", overflow: "hidden" }}>
                <InfoTag>
                  <Typography variant="textBase500" color={theme.palette.dsBase.v200}>
                    {challengeStatusTest}
                  </Typography>
                </InfoTag>
              </ChallengeCardSimpleTopRight>
            )}
            {!published && (
              <NotPublishedTag>
                <Typography variant="textLg600" color={theme.palette.dsBase.v100}>
                  {i18n("not-published")}
                </Typography>
              </NotPublishedTag>
            )}
            {published && challengeHasTag(challenge, "BETA_CONTENT") && (
              <BetaTag>
                <Typography variant="textLg600" color={theme.palette.dsBase.v100}>
                  {i18n("BETA_CONTENT")}
                </Typography>
              </BetaTag>
            )}
          </Box>
        </BybConditionalWrapper>
        <Stack spacing={0.5} sx={{ marginTop: theme.spacing(2), textAlign: "center", flexGrow: 1, opacity: isDisabled ? 0.3 : 1 }}>
          <Typography variant="text1xl500" color="dsBase.v200">
            {t(`${locizeKeyPrefix}.name`, `${title}`)}
          </Typography>
          {division && (
            <Typography variant="textBase500" color={isDisabled ? "dsBase.v200" : "dsNeutral.v300"}>
              {`${i18n(`league.${league}.short`)} · ${i18n("division.specific", { division })}`}
            </Typography>
          )}
        </Stack>
      </ChallengeCardSimpleButtonBase>
    </HapticsButtonWrapper>
  );
};

export default ChallengeCardSimple;
